import { getCookie } from '../core/utils.js';

/*
  landing-intent.js

  Ringkasan:
  Menandai visitor berdasarkan halaman LANDING-nya (mis. /fitur/payroll-service/),
  lalu mengisi placeholder {intent} di pesan WABA dengan teks dari rule tersebut —
  walaupun tombol WABA diklik di halaman lain (mis. halaman Talenta umum).

  Alur:
  1) initLandingIntent() (dipanggil saat page load dari waba-core.js):
     - fitur aktif & cookie belum ada (first-touch) & halaman ini awal sesi
       (atau mode 'any_page') & path cocok rule → set cookie host-only
       `mlat_landing_intent` = key rule, TTL = ttlDays (sama dengan cookie attribution lain).
  2) applyIntentPlaceholder() (dipanggil waba-trigger.js setelah $url final dibangun):
     - ganti {intent} / %7Bintent%7D dengan teks rule sesuai bahasa halaman klik,
       atau string kosong kalau tidak ada intent / fitur off / klik terjadi di
       halaman intent itu sendiri.

  Config: window.mlatConfig.waba.landingIntent = { enabled, mode, rules: [{ key, urls, texts }] }
  (diisi dari wp-admin → Leads Attribution → WABA Tracking → Landing Intent Rules).

  Cookie sengaja HOST-ONLY (tanpa Domain=) — sama alasannya dengan waba_code:
  intent milik satu situs tidak boleh bocor ke situs Mekari lain di root domain yang sama.
*/

const COOKIE_NAME = 'mlat_landing_intent';
const PLACEHOLDER_RE = /\{intent\}|%7Bintent%7D/gi;

const getConfig = () =>
  (window.mlatConfig && window.mlatConfig.waba && window.mlatConfig.waba.landingIntent) || {};

// Same normalization as mlat_normalize_intent_path() in PHP: lowercase, no
// query/hash, wrapped in slashes — so "/a/b" and "/a/b/" match, "/a/b-x/" doesn't.
const normalizePath = (p) => {
  const s = String(p || '').split(/[?#]/)[0].trim().toLowerCase();
  return '/' + s.replace(/^\/+|\/+$/g, '') + '/';
};

const ruleMatchesPath = (rule, path) =>
  Array.isArray(rule && rule.urls) && rule.urls.some((u) => u && path.indexOf(normalizePath(u)) !== -1);

// "Awal sesi" = no referrer (direct, app, stripped referrer) or a referrer
// from another host (Google, ads, other Mekari sites). Same-host referrer means
// the visitor navigated here from inside this site.
const isSessionEntry = () => {
  const ref = document.referrer || '';
  if (!ref) return true;
  try {
    return new URL(ref).host !== window.location.host;
  } catch (e) {
    return true;
  }
};

const setHostOnlyCookie = (name, value, days) => {
  const expires = new Date(Date.now() + Math.max(1, days) * 864e5).toUTCString();
  let c = name + '=' + encodeURIComponent(value) + '; Expires=' + expires + '; Path=/; SameSite=Lax';
  if (window.location.protocol === 'https:') c += '; Secure';
  document.cookie = c; // intentionally NO Domain= → host-only
};

export function initLandingIntent() {
  // runCoreInits() runs twice — the decision only needs to be made once per page load.
  if (window.__mlatLandingIntentInit) return;
  window.__mlatLandingIntentInit = true;

  const cfg = getConfig();
  if (!cfg.enabled || !Array.isArray(cfg.rules) || !cfg.rules.length) return;

  // Step 1 — first-touch: an existing intent is never overwritten.
  if (getCookie(COOKIE_NAME)) return;

  // Step 2 — landing mode: default only the first page of a session counts.
  if (cfg.mode !== 'any_page' && !isSessionEntry()) return;

  // Step 3 — first rule (admin order) whose URL list matches this page wins.
  const path = normalizePath(window.location.pathname);
  const rule = cfg.rules.find((r) => ruleMatchesPath(r, path));
  if (!rule || !rule.key) return;

  const ttl = (window.mlatConfig && parseInt(window.mlatConfig.ttlDays, 10)) || 31;
  setHostOnlyCookie(COOKIE_NAME, rule.key, ttl);
  if (window.__jurnalWabaDebug) console.log('[mlat intent] landing intent set:', rule.key, '| path:', path, '| ttl:', ttl);
}

/**
 * Text that should replace {intent} for a WABA click on the current page, in `lang`.
 * '' when: feature off, no intent cookie, cookie key no longer matches a rule
 * (renamed/deleted), the click happens on that intent's own page, or no text for
 * `lang` and no `default` line (deliberately no fallback to another language).
 */
export function resolveIntentText(lang) {
  const cfg = getConfig();
  if (!cfg.enabled || !Array.isArray(cfg.rules)) return '';

  let key = getCookie(COOKIE_NAME) || '';
  try { key = decodeURIComponent(key); } catch (e) {}
  if (!key) return '';

  const rule = cfg.rules.find((r) => r && r.key === key);
  if (!rule) return '';

  // Buttons on the intent page itself already carry their own (intent-specific) text/number.
  if (ruleMatchesPath(rule, normalizePath(window.location.pathname))) return '';

  const texts = rule.texts || {};
  const l = String(lang || '').toLowerCase();
  if (Object.prototype.hasOwnProperty.call(texts, l)) return String(texts[l] || '');
  if (Object.prototype.hasOwnProperty.call(texts, 'default')) return String(texts['default'] || '');
  return '';
}

// URL-encode the intent text for the query string (so "&", "#", "+" can't break
// the wa.me / api.whatsapp.com URL) while keeping literal %XX tokens the admin
// typed on purpose — %20 (space) and %0A (WhatsApp line break), same convention
// as the other message template fields.
const encodeKeepingEscapes = (text) =>
  String(text || '')
    .split(/(%[0-9A-Fa-f]{2})/)
    .map((part, i) => (i % 2 ? part : encodeURIComponent(part)))
    .join('');

/**
 * Replace every {intent} / %7Bintent%7D in the final WABA URL. Runs on the whole
 * URL, so the placeholder works wherever the admin put it (global template, kw/qkw
 * template, or a text-waba button's own href text). Always strips the placeholder,
 * so a literal "{intent}" never reaches WhatsApp.
 */
export function applyIntentPlaceholder(url, lang) {
  const str = String(url || '');
  if (str.search(PLACEHOLDER_RE) === -1) return str;
  const encoded = encodeKeepingEscapes(resolveIntentText(lang));
  if (window.__jurnalWabaDebug) console.log('[mlat intent] {intent} →', encoded || '(kosong)', '| lang:', lang);
  return str.replace(PLACEHOLDER_RE, () => encoded);
}
